import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Translatable } from '@hrlabs/shared-types';
import { getRow, listCollection, saveRow } from '../../lib/db';
import { nextOrder } from '../../lib/order';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';

const EMPTY: Translatable = { en: '', bg: '' };
const collectionFor = (kind?: string) =>
  kind === 'candidate' ? 'candidateTestimonials' : 'businessPartnerTestimonials';

export function TestimonialEdit() {
  const { kind, id } = useParams();
  const col = collectionFor(kind);
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState<Translatable>({ ...EMPTY });
  const [position, setPosition] = useState<Translatable>({ ...EMPTY });
  const [quote, setQuote] = useState<Translatable>({ ...EMPTY });
  const [order, setOrder] = useState('');

  useEffect(() => {
    if (!id) return;
    getRow(col, id).then((r) => {
      if (r) {
        setName((r.name as Translatable) ?? { ...EMPTY });
        setPosition((r.position as Translatable) ?? { ...EMPTY });
        setQuote((r.quote as Translatable) ?? { ...EMPTY });
        setOrder(typeof r.order === 'number' ? String(r.order) : '');
      }
      setLoading(false);
    });
  }, [col, id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.bg.trim() || !position.bg.trim() || !quote.bg.trim()) {
      notify('danger', 'Bulgarian name, position and quote are required.');
      return;
    }
    setSaving(true);
    try {
      const savedId = await saveRow(col, id, {
        name,
        position,
        quote,
        ...(order.trim() !== ''
          ? { order: Number(order) }
          : id
            ? {}
            : { order: nextOrder(await listCollection(col)) }),
      });
      notify('success', 'Testimonial saved.');
      navigate(`/testimonials/${kind}/${savedId}`);
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 760 }}>
      <h3 className="fw-bold mb-3">{id ? 'Edit testimonial' : 'New testimonial'}</h3>
      <BilingualField label="Name" value={name} onChange={setName} required rows={1} />
      <BilingualField label="Position" value={position} onChange={setPosition} required rows={1} />
      <BilingualField label="Quote" value={quote} onChange={setQuote} required />
      <div className="mb-3" style={{ maxWidth: 160 }}>
        <label className="form-label fw-semibold">Order</label>
        <input type="number" className="form-control" value={order} onChange={(e) => setOrder(e.target.value)} />
        <div className="form-text">Lower numbers first (the site shows the first two).</div>
      </div>
      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
